/**
 * Decision staleness verification (Task 3 — "Kage"-inspired).
 *
 * A decision linked to a `symbol_id` keeps surfacing even after that symbol is
 * renamed, moved, or deleted, and even after its source body changes materially
 * since the decision was captured. This module checks, at recall time, whether a
 * decision's linked code still resolves AND still looks the way it did when the
 * decision was made.
 *
 * Verdicts:
 *   - `"ok"`             — symbol resolves and source slice is unchanged since
 *                          `created_at` (or we couldn't tell → fail-open).
 *   - `"symbol_missing"` — the linked `symbol_id` no longer resolves in the
 *                          index (deleted / renamed / moved).
 *   - `"code_changed"`   — the symbol resolves, but its current source slice
 *                          differs from the slice at the last commit ≤ the
 *                          decision's `created_at`.
 *
 * Design notes:
 *   - Conservative by default. Git unavailable, file untracked, ambiguous
 *     history, unparseable timestamps → `"ok"` (never withhold on uncertainty).
 *   - Decisions with no `symbol_id` are always `"ok"`: a bare `file_path` link
 *     is too coarse to flag reliably without a lot of false positives.
 *   - Pure + synchronous (mirrors `getSymbol`'s `verify_against_git` path) so
 *     callers can fold it into existing synchronous recall handlers. The git
 *     calls are `execFileSync` with a short timeout — fine for the occasional
 *     verification pass, not the hot loop.
 */
import { execFileSync } from 'node:child_process';
import { readFileSync } from 'node:fs';
import path from 'node:path';
import type { Store } from '../db/store.js';
import { safeGitEnv } from '../utils/git-env.js';
import type { DecisionRow } from './decision-types.js';

export type VerificationStatus = 'ok' | 'symbol_missing' | 'code_changed';

export interface DecisionVerification {
  /** Verdict — see module docstring. */
  verification: VerificationStatus;
  /** True when the linked code is missing or materially changed. */
  stale: boolean;
}

/** A decision row decorated with its verification verdict (when computed). */
export type VerifiedDecisionRow = DecisionRow & Partial<DecisionVerification>;

const FRESH: DecisionVerification = { verification: 'ok', stale: false };

/**
 * Per-batch memo cache. Verification is O(N) synchronous git subprocess spawns
 * (up to 2 per anchored decision: `git log` + `git show`), which blocks the
 * event loop and can push a large page toward the recall timeout. Decisions
 * frequently cluster on the same file and/or the same `created_at`, so caching
 * the two git results by their input tuple collapses the duplicate spawns to a
 * single one. Pure optimization — identical verdicts, fewer processes.
 *
 * `null` values are cached too (a miss is as expensive to recompute as a hit),
 * so we distinguish "not yet computed" (absent key) from "computed → null"
 * (present key, value null).
 */
export interface VerifyCache {
  /** key: `${relPath} ${isoTs}` → last commit SHA or null. */
  commitByFileTs: Map<string, string | null>;
  /** key: `${commit} ${relPath} ${byteStart} ${byteEnd}` → slice or null. */
  sliceByCommit: Map<string, string | null>;
}

/** Build a fresh empty per-batch verification cache. */
export function createVerifyCache(): VerifyCache {
  return { commitByFileTs: new Map(), sliceByCommit: new Map() };
}

/**
 * Read the on-disk byte slice [byteStart, byteEnd) for a file relative to root.
 * Returns null on any IO error so callers fail-open.
 */
function readDiskSlice(
  rootPath: string,
  relPath: string,
  byteStart: number,
  byteEnd: number,
): string | null {
  try {
    const buf = readFileSync(path.resolve(rootPath, relPath));
    return buf.slice(byteStart, byteEnd).toString('utf8');
  } catch {
    return null;
  }
}

/**
 * Resolve the last commit SHA that touched `relPath` at or before `isoTs`.
 * Uses `git log -1 --before` so we land on the state the decision was made
 * against. Returns null when git is unavailable, the file is untracked, the
 * timestamp is unparseable, or there is no such commit.
 */
function lastCommitBefore(
  rootPath: string,
  relPath: string,
  isoTs: string,
  cache?: VerifyCache,
): string | null {
  const key = `${relPath} ${isoTs}`;
  if (cache?.commitByFileTs.has(key)) return cache.commitByFileTs.get(key) ?? null;
  const value = computeLastCommitBefore(rootPath, relPath, isoTs);
  cache?.commitByFileTs.set(key, value);
  return value;
}

function computeLastCommitBefore(rootPath: string, relPath: string, isoTs: string): string | null {
  const ts = Date.parse(isoTs);
  if (!Number.isFinite(ts)) return null;
  try {
    const out = execFileSync(
      'git',
      ['log', '-1', `--before=${new Date(ts).toISOString()}`, '--format=%H', '--', relPath],
      {
        cwd: rootPath,
        stdio: ['ignore', 'pipe', 'ignore'],
        timeout: 3000,
        env: safeGitEnv(),
        encoding: 'utf8',
      },
    ).trim();
    return out.length > 0 ? out : null;
  } catch {
    return null;
  }
}

/**
 * Read the byte slice [byteStart, byteEnd) from `<commit>:<relPath>`.
 * Returns null on any git error (file not in that commit, etc.).
 */
function readCommitSlice(
  rootPath: string,
  commit: string,
  relPath: string,
  byteStart: number,
  byteEnd: number,
  cache?: VerifyCache,
): string | null {
  const key = `${commit} ${relPath} ${byteStart} ${byteEnd}`;
  if (cache?.sliceByCommit.has(key)) return cache.sliceByCommit.get(key) ?? null;
  const value = computeReadCommitSlice(rootPath, commit, relPath, byteStart, byteEnd);
  cache?.sliceByCommit.set(key, value);
  return value;
}

function computeReadCommitSlice(
  rootPath: string,
  commit: string,
  relPath: string,
  byteStart: number,
  byteEnd: number,
): string | null {
  try {
    const raw = execFileSync('git', ['show', `${commit}:${relPath}`], {
      cwd: rootPath,
      stdio: ['ignore', 'pipe', 'ignore'],
      timeout: 3000,
      env: safeGitEnv(),
      encoding: 'buffer',
    });
    return raw.slice(byteStart, byteEnd).toString('utf8');
  } catch {
    return null;
  }
}

/**
 * Verify a single decision against the live code index + git history.
 *
 * @param decision  The decision row to verify.
 * @param store     The code Store (the index — NOT the decision store).
 * @param projectRoot Absolute path to the project root for git/IO resolution.
 */
export function verifyDecision(
  decision: Pick<DecisionRow, 'symbol_id' | 'file_path' | 'created_at'>,
  store: Store,
  projectRoot: string,
  cache?: VerifyCache,
): DecisionVerification {
  // No code anchor → nothing to verify. File-only links are intentionally
  // not flagged (too coarse, high false-positive rate).
  if (!decision.symbol_id) return FRESH;

  // Fail-open safety net (correctness-critical). Every code/git access below
  // can throw — a locked or corrupt index DB, an internal Store invariant
  // violation, a git subprocess blowing up in an unexpected way. The module
  // contract is: verification NEVER hides a decision on its own failure. An
  // uncaught throw here would propagate up through `verifyDecisions` and crash
  // the whole recall (query_decisions / get_wake_up), turning a soft "couldn't
  // verify" into hard data loss. So any unexpected error fails open to FRESH.
  try {
    const symbol = store.getSymbolBySymbolId(decision.symbol_id);
    if (!symbol) {
      // The decision points at a symbol the index no longer knows — renamed,
      // moved, or deleted. Withhold/flag.
      return { verification: 'symbol_missing', stale: true };
    }

    const file = store.getFileById(symbol.file_id);
    if (!file) {
      // Symbol row exists but its file is gone from the index — treat as missing.
      return { verification: 'symbol_missing', stale: true };
    }

    // Compare the current on-disk slice against the slice at the last commit
    // ≤ created_at. Any read failure fails-open to "ok".
    const current = readDiskSlice(projectRoot, file.path, symbol.byte_start, symbol.byte_end);
    if (current === null) return FRESH;

    const commit = lastCommitBefore(projectRoot, file.path, decision.created_at, cache);
    if (!commit) return FRESH;

    // The byte range is the CURRENT index range; the historical file may have
    // had different offsets, so a byte-range slice of an old blob is only a
    // heuristic. To keep false positives low we compare on the historical
    // commit's own byte range when the slice lands cleanly; if the old slice
    // read fails we fail-open. This is intentionally conservative — it flags
    // the common case (symbol body edited in place) without crying wolf on
    // offset drift.
    const past = readCommitSlice(
      projectRoot,
      commit,
      file.path,
      symbol.byte_start,
      symbol.byte_end,
      cache,
    );
    if (past === null) return FRESH;

    if (normalize(past) !== normalize(current)) {
      return { verification: 'code_changed', stale: true };
    }
    return FRESH;
  } catch {
    // Fail-open: an internal error must never hide a decision.
    return FRESH;
  }
}

/**
 * Batch-read multiple `<commit>:<relPath>` blobs in a SINGLE `git cat-file
 * --batch` process, byte-slicing each into the shared `sliceByCommit` cache.
 *
 * The scattered worst case (N decisions on N distinct files) defeats the memo
 * cache — every (commit, slice) key is unique — so the per-row path would
 * otherwise spawn one `git show` per decision. This collapses all of those
 * blob reads into one subprocess. This is the `git show` half of the batching;
 * the commit lookups are batched separately by `batchResolveCommitsByFile`
 * (one `git log` per distinct file, resolved in-process via binary search).
 *
 * Fail-open: any error (git missing, blob absent, malformed stream) leaves the
 * relevant `sliceByCommit` entries unset, so the per-row path falls back to a
 * normal `git show` — never a wrong verdict, only a lost optimization.
 *
 * @param specs Distinct blob reads: each needs `commit`, `relPath`, and the
 *              byte range to extract, plus the `sliceKey` to store under.
 */
function batchReadCommitSlices(
  rootPath: string,
  specs: Array<{
    commit: string;
    relPath: string;
    byteStart: number;
    byteEnd: number;
    sliceKey: string;
  }>,
  cache: VerifyCache,
): void {
  if (specs.length === 0) return;

  // De-duplicate the blob reads by (commit, relPath): two symbols in the same
  // file+commit share one blob, sliced twice in-process.
  const blobKey = (commit: string, relPath: string) => `${commit}:${relPath}`;
  const uniqueBlobs = new Map<string, { commit: string; relPath: string }>();
  for (const s of specs) {
    uniqueBlobs.set(blobKey(s.commit, s.relPath), { commit: s.commit, relPath: s.relPath });
  }
  const order = [...uniqueBlobs.values()];
  // Pass stdin as a Buffer so we can request a Buffer response (`encoding:
  // 'buffer'`) — mixing a string `input` with a buffer output encoding throws
  // "Unknown encoding: buffer" in Node.
  const stdin = Buffer.from(order.map((b) => `${b.commit}:${b.relPath}`).join('\n') + '\n', 'utf8');

  let raw: Buffer;
  try {
    raw = execFileSync('git', ['cat-file', '--batch'], {
      cwd: rootPath,
      input: stdin,
      stdio: ['pipe', 'pipe', 'ignore'],
      timeout: 5000,
      env: safeGitEnv(),
      maxBuffer: 64 * 1024 * 1024,
      encoding: 'buffer',
    });
  } catch {
    // Whole batch failed — leave cache unset so per-row falls back to git show.
    return;
  }

  // Parse the `--batch` stream. For each request line git emits either:
  //   "<oid> <type> <size>\n" followed by <size> bytes of content and "\n", or
  //   "<request> missing\n"
  // We match responses to requests positionally (git preserves input order).
  const blobContent = new Map<string, Buffer | null>();
  let offset = 0;
  for (const b of order) {
    const key = blobKey(b.commit, b.relPath);
    // Read one header line.
    const nl = raw.indexOf(0x0a, offset);
    if (nl === -1) {
      blobContent.set(key, null);
      continue;
    }
    const header = raw.toString('utf8', offset, nl);
    offset = nl + 1;
    const parts = header.split(' ');
    if (parts.length < 3 || parts[parts.length - 1] === 'missing') {
      // "<request> missing" (no content follows).
      blobContent.set(key, null);
      continue;
    }
    const size = Number.parseInt(parts[2], 10);
    if (!Number.isFinite(size) || size < 0 || offset + size > raw.length) {
      blobContent.set(key, null);
      continue;
    }
    blobContent.set(key, raw.subarray(offset, offset + size));
    // Content is followed by a trailing newline git appends.
    offset += size + 1;
  }

  // Slice each requested range into the shared cache. Absent/missing blobs are
  // intentionally NOT cached (leave the key unset → per-row git show fallback).
  for (const s of specs) {
    if (cache.sliceByCommit.has(s.sliceKey)) continue;
    const buf = blobContent.get(blobKey(s.commit, s.relPath));
    if (buf === undefined || buf === null) continue;
    cache.sliceByCommit.set(s.sliceKey, buf.subarray(s.byteStart, s.byteEnd).toString('utf8'));
  }
}

/**
 * Fetch a file's full commit history (sha + author date) in ONE `git log`
 * spawn, ascending by timestamp so `findLastCommitAtOrBefore` can binary
 * search it. Returns null on any error (git unavailable, untracked file, huge
 * history that blows `maxBuffer`) so callers fall back to the per-decision
 * `git log --before` lookup.
 */
function fetchFileCommitHistory(
  rootPath: string,
  relPath: string,
): Array<{ sha: string; ts: number }> | null {
  try {
    const out = execFileSync('git', ['log', '--format=%H %cI', '--', relPath], {
      cwd: rootPath,
      stdio: ['ignore', 'pipe', 'ignore'],
      timeout: 5000,
      env: safeGitEnv(),
      encoding: 'utf8',
      maxBuffer: 64 * 1024 * 1024,
    });
    const commits: Array<{ sha: string; ts: number }> = [];
    for (const line of out.split('\n')) {
      if (!line) continue;
      const sp = line.indexOf(' ');
      if (sp === -1) continue;
      const ts = Date.parse(line.slice(sp + 1));
      if (!Number.isFinite(ts)) continue;
      commits.push({ sha: line.slice(0, sp), ts });
    }
    // `git log` emits newest-first; reverse to ascending for binary search.
    commits.reverse();
    return commits;
  } catch {
    return null;
  }
}

/** Binary search an ascending-by-`ts` commit list for the last commit ≤ `targetTs`. */
function findLastCommitAtOrBefore(
  commits: Array<{ sha: string; ts: number }>,
  targetTs: number,
): string | null {
  let lo = 0;
  let hi = commits.length - 1;
  let result: string | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (commits[mid].ts <= targetTs) {
      result = commits[mid].sha;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result;
}

/**
 * Batch-resolve `lastCommitBefore` for every distinct `relPath` touched by
 * the decision batch: ONE `git log` per distinct file (not one `git log
 * --before` per (file, created_at) pair), then an in-process binary search
 * per decision. Populates `commitByFileTs` directly; entries this can't
 * resolve are simply left unset so `lastCommitBefore`'s per-row path falls
 * back to its own `git log --before` spawn — never a wrong verdict, only a
 * lost optimization.
 *
 * This is the commit-lookup half of the same batching `batchReadCommitSlices`
 * already does for blob reads — together they bound the scattered worst case
 * (N decisions on N distinct files) at O(distinct files) git spawns instead
 * of O(N).
 */
function batchResolveCommitsByFile(
  rootPath: string,
  entries: Array<{ relPath: string; createdAt: string }>,
  cache: VerifyCache,
): void {
  const pending = new Map<string, Set<string>>(); // relPath -> uncached created_at values
  for (const { relPath, createdAt } of entries) {
    const key = `${relPath} ${createdAt}`;
    if (cache.commitByFileTs.has(key)) continue;
    let set = pending.get(relPath);
    if (!set) {
      set = new Set();
      pending.set(relPath, set);
    }
    set.add(createdAt);
  }

  for (const [relPath, timestamps] of pending) {
    const history = fetchFileCommitHistory(rootPath, relPath);
    if (!history) continue; // fail-open: per-row lastCommitBefore retries individually
    for (const isoTs of timestamps) {
      const ts = Date.parse(isoTs);
      const key = `${relPath} ${isoTs}`;
      cache.commitByFileTs.set(
        key,
        Number.isFinite(ts) ? findLastCommitAtOrBefore(history, ts) : null,
      );
    }
  }
}

/** Trim trailing whitespace per line + collapse CRLF so cosmetic diffs don't flag. */
function normalize(s: string): string {
  return s
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+$/gm, '')
    .trim();
}

/**
 * Decorate a batch of decisions with verification verdicts.
 *
 * @param decisions   Rows to verify (typically a `query_decisions` page).
 * @param store       The code Store, or null when no index is available — in
 *                    which case every row is returned untouched (no verdict).
 * @param projectRoot Absolute project root.
 * @param opts.withhold When true, drop stale rows entirely instead of flagging
 *                      them. Default false (annotate in place).
 * @returns The decorated (and possibly filtered) rows.
 */
export function verifyDecisions(
  decisions: DecisionRow[],
  store: Store | null,
  projectRoot: string,
  opts: { withhold?: boolean } = {},
): VerifiedDecisionRow[] {
  if (!store || decisions.length === 0) return decisions;
  // One shared memo cache for the whole batch collapses duplicate git spawns
  // when decisions cluster on the same file / commit slice.
  const cache = createVerifyCache();

  // Pre-pass: resolve every anchored decision's symbol/file once, batch the
  // commit lookup (ONE `git log` per distinct file, via
  // `batchResolveCommitsByFile`) and the blob reads (ONE `git cat-file
  // --batch` for the whole set, via `batchReadCommitSlices`). This is the win
  // for the scattered worst case (N distinct files) — without it the per-row
  // loop below would spawn up to 2 git processes per decision. Fail-open: any
  // resolution error just leaves the relevant cache entries unset and the
  // per-row path falls back to its normal `git log --before` / `git show`.
  try {
    const resolved: Array<{
      decision: DecisionRow;
      relPath: string;
      byteStart: number;
      byteEnd: number;
    }> = [];
    for (const d of decisions) {
      if (!d.symbol_id) continue;
      const symbol = store.getSymbolBySymbolId(d.symbol_id);
      if (!symbol) continue;
      const file = store.getFileById(symbol.file_id);
      if (!file) continue;
      resolved.push({
        decision: d,
        relPath: file.path,
        byteStart: symbol.byte_start,
        byteEnd: symbol.byte_end,
      });
    }

    batchResolveCommitsByFile(
      projectRoot,
      resolved.map((r) => ({ relPath: r.relPath, createdAt: r.decision.created_at })),
      cache,
    );

    const specs: Array<{
      commit: string;
      relPath: string;
      byteStart: number;
      byteEnd: number;
      sliceKey: string;
    }> = [];
    const seenSliceKeys = new Set<string>();
    for (const r of resolved) {
      const commit = lastCommitBefore(projectRoot, r.relPath, r.decision.created_at, cache);
      if (!commit) continue;
      const sliceKey = `${commit} ${r.relPath} ${r.byteStart} ${r.byteEnd}`;
      if (seenSliceKeys.has(sliceKey)) continue;
      seenSliceKeys.add(sliceKey);
      specs.push({
        commit,
        relPath: r.relPath,
        byteStart: r.byteStart,
        byteEnd: r.byteEnd,
        sliceKey,
      });
    }
    batchReadCommitSlices(projectRoot, specs, cache);
  } catch {
    // Fail-open: fall through to the per-row path with whatever cache we have.
  }

  const out: VerifiedDecisionRow[] = [];
  for (const d of decisions) {
    // Bare decisions (no symbol_id) skip the git work entirely.
    if (!d.symbol_id) {
      out.push(d);
      continue;
    }
    // Belt-and-suspenders: `verifyDecision` already fails open, but a per-row
    // guard here guarantees one poisoned row can never crash the whole batch
    // (and thus the whole recall). On any unexpected error, serve the row
    // unflagged rather than dropping it.
    let v: DecisionVerification = FRESH;
    try {
      v = verifyDecision(d, store, projectRoot, cache);
    } catch {
      v = FRESH;
    }
    if (v.stale && opts.withhold) continue;
    if (v.stale) {
      out.push({ ...d, verification: v.verification, stale: true });
    } else {
      out.push(d);
    }
  }
  return out;
}
